/**
 * CLIENT MOCK DATA
 *
 * Centralized mock data for clients including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

/**
 * DEFAULT CLIENT LIST VIEWS
 */
export const defaultClientListViews: ListView[] = [
  {
    id: 'lv_clients_all',
    name: 'All Clients',
    apiName: 'all_clients',
    objectApiName: 'Client',
    description: 'All client records',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Client ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', isVisible: true, order: 4 },
      { fieldApiName: 'industry', fieldLabel: 'Industry', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 6 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_clients_active',
    name: 'Active Clients',
    apiName: 'active_clients',
    objectApiName: 'Client',
    description: 'Clients with Active status',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'industry', fieldLabel: 'Industry', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'Active' }
    ],
    createdBy: 'Sarah Johnson',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_clients_recent',
    name: 'Recently Created',
    apiName: 'recent_clients',
    objectApiName: 'Client',
    description: 'Clients created in the last 30 days',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'desc'
    },
    createdBy: 'Michael Chen',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_clients_my',
    name: 'My Clients',
    apiName: 'my_clients',
    objectApiName: 'Client',
    description: 'Clients I own',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 3 },
    ],
    filters: [
      { fieldApiName: 'accountOwner', operator: 'equals', value: 'Current User' }
    ],
    createdBy: 'Sarah Johnson',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

/**
 * DEFAULT CLIENT RECORDS
 */
export const defaultClientRecords: ListViewRecord[] = [
  {
    id: 'client_001',
    name: 'John Smith',
    email: 'john.smith@email.com',
    phone: '(555) 123-4567',
    status: 'Active',
    accountId: 'acc_001',
    accountOwner: 'Sarah Johnson',
    owner: 'Sarah Johnson',
    accountManager: 'Mike Davis',
    accountName: 'Smith Personal Account',
    industry: 'Technology',
    lastContact: '2025-01-04T10:00:00Z',
    createdDate: '2024-01-15T10:30:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-04T10:00:00Z',
    lastModifiedBy: 'user_sarah',
    activeProjects: 3,
    annualRevenue: 85000,
    auditId: 'AUD-2024-001',
    auditStatus: 'Completed',
    billingAddress: '123 Oak Street, Austin, TX 78701',
    contractEndDate: '2025-12-31',
    // Credit Scores
    initialScore: 580,
    currentScore: 685,
    targetScore: 720,
    scoreImprovement: 105
  },
  {
    id: 'client_002',
    name: 'Acme Corporation',
    email: 'contact@acme.com',
    phone: '(555) 234-5678',
    status: 'Active',
    accountId: 'acc_001',
    accountOwner: 'Michael Chen',
    owner: 'Michael Chen',
    accountManager: 'Lisa Brown',
    accountName: 'Acme Corp Enterprise',
    industry: 'Manufacturing',
    lastContact: '2025-01-03T14:20:00Z',
    createdDate: '2024-02-20T09:15:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-03T14:20:00Z',
    lastModifiedBy: 'user_michael',
    activeProjects: 5,
    annualRevenue: 2500000,
    auditId: 'AUD-2024-002',
    auditStatus: 'In Progress',
    billingAddress: '456 Industrial Blvd, Detroit, MI 48201',
    contractEndDate: '2026-06-30',
    // Credit Scores
    initialScore: 620,
    currentScore: 710,
    targetScore: 750,
    scoreImprovement: 90
  },
  {
    id: 'client_003',
    name: 'Global Industries Inc',
    email: 'info@globalind.com',
    phone: '(555) 345-6789',
    status: 'Inactive',
    accountOwner: 'Sarah Johnson',
    owner: 'Sarah Johnson',
    accountManager: 'Tom Wilson',
    accountName: 'Global Industries',
    industry: 'Retail',
    lastContact: '2024-11-15T11:00:00Z',
    createdDate: '2023-11-05T08:45:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2024-11-15T11:00:00Z',
    lastModifiedBy: 'user_sarah',
    activeProjects: 0,
    annualRevenue: 1800000,
    auditId: 'AUD-2023-015',
    auditStatus: 'Completed',
    billingAddress: '789 Commerce Way, Chicago, IL 60601',
    contractEndDate: '2024-12-31'
  },
  {
    id: 'client_004',
    name: 'Tech Startup LLC',
    email: 'hello@techstartup.io',
    phone: '(555) 456-7890',
    status: 'Active',
    accountOwner: 'David Wilson',
    owner: 'David Wilson',
    accountManager: 'Sarah Johnson',
    accountName: 'Tech Startup Account',
    industry: 'Technology',
    lastContact: '2025-01-02T09:30:00Z',
    createdDate: '2024-03-10T11:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-02T09:30:00Z',
    lastModifiedBy: 'user_david',
    activeProjects: 2,
    annualRevenue: 350000,
    auditId: 'AUD-2024-003',
    auditStatus: 'Not Started',
    billingAddress: '101 Innovation Drive, San Jose, CA 95110',
    contractEndDate: '2025-09-30',
    // Credit Scores
    initialScore: 550,
    currentScore: 650,
    targetScore: 700,
    scoreImprovement: 100
  },
  {
    id: 'client_005',
    name: 'Financial Services Group',
    email: 'contact@finservices.com',
    phone: '(555) 567-8901',
    status: 'Active',
    accountOwner: 'Emily Davis',
    owner: 'Emily Davis',
    accountManager: 'Michael Chen',
    accountName: 'FSG Enterprise',
    industry: 'Finance',
    lastContact: '2025-01-01T16:45:00Z',
    createdDate: '2023-09-12T13:30:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-01T16:45:00Z',
    lastModifiedBy: 'user_emily',
    activeProjects: 4,
    annualRevenue: 5000000,
    auditId: 'AUD-2024-004',
    auditStatus: 'Under Review',
    billingAddress: '200 Wall Street, New York, NY 10005',
    contractEndDate: '2026-03-31'
  },
  {
    id: 'client_006',
    name: 'Retail Solutions Co',
    email: 'sales@retailsolutions.com',
    phone: '(555) 678-9012',
    status: 'Pending',
    accountOwner: 'Michael Chen',
    owner: 'Michael Chen',
    accountManager: 'Emily Davis',
    accountName: 'Retail Solutions',
    industry: 'Retail',
    lastContact: '2024-12-28T10:15:00Z',
    createdDate: '2024-06-15T14:00:00Z',
    createdBy: 'user_michael',
    lastModifiedDate: '2024-12-28T10:15:00Z',
    lastModifiedBy: 'user_michael',
    activeProjects: 1,
    annualRevenue: 750000,
    auditId: 'AUD-2024-005',
    auditStatus: 'Not Started',
    billingAddress: '333 Retail Lane, Dallas, TX 75201',
    contractEndDate: '2025-06-30'
  },
  {
    id: 'client_007',
    name: 'Healthcare Partners',
    email: 'info@healthcarepartners.com',
    phone: '(555) 789-0123',
    status: 'Active',
    accountOwner: 'Sarah Johnson',
    owner: 'Sarah Johnson',
    accountManager: 'David Wilson',
    accountName: 'Healthcare Partners LLC',
    industry: 'Healthcare',
    lastContact: '2025-01-03T08:15:00Z',
    createdDate: '2024-04-22T10:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-03T08:15:00Z',
    lastModifiedBy: 'user_sarah',
    activeProjects: 3,
    annualRevenue: 3200000,
    auditId: 'AUD-2024-006',
    auditStatus: 'Completed',
    billingAddress: '500 Medical Center Dr, Boston, MA 02108',
    contractEndDate: '2026-01-31'
  },
  {
    id: 'client_008',
    name: 'Education First',
    email: 'contact@educationfirst.edu',
    phone: '(555) 890-1234',
    status: 'Active',
    accountOwner: 'David Wilson',
    owner: 'David Wilson',
    accountManager: 'Lisa Brown',
    accountName: 'Education First Institute',
    industry: 'Education',
    lastContact: '2024-12-30T13:45:00Z',
    createdDate: '2024-05-18T09:30:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2024-12-30T13:45:00Z',
    lastModifiedBy: 'user_david',
    activeProjects: 2,
    annualRevenue: 1200000,
    auditId: 'AUD-2024-007',
    auditStatus: 'In Progress',
    billingAddress: '600 University Ave, Cambridge, MA 02139',
    contractEndDate: '2025-08-31'
  },
  {
    id: 'client_009',
    name: 'Construction Co',
    email: 'info@constructionco.com',
    phone: '(555) 901-2345',
    status: 'Active',
    accountOwner: 'Michael Chen',
    owner: 'Michael Chen',
    accountManager: 'Tom Wilson',
    accountName: 'Construction Co LLC',
    industry: 'Construction',
    lastContact: '2025-01-04T14:00:00Z',
    createdDate: '2024-07-10T11:15:00Z',
    createdBy: 'user_michael',
    lastModifiedDate: '2025-01-04T14:00:00Z',
    lastModifiedBy: 'user_michael',
    activeProjects: 6,
    annualRevenue: 4500000,
    auditId: 'AUD-2024-008',
    auditStatus: 'Not Started',
    billingAddress: '700 Builder Blvd, Phoenix, AZ 85001',
    contractEndDate: '2025-12-31'
  },
  {
    id: 'client_010',
    name: 'Legal Associates',
    email: 'contact@legalassociates.com',
    phone: '(555) 012-3456',
    status: 'Active',
    accountOwner: 'Emily Davis',
    owner: 'Emily Davis',
    accountManager: 'Sarah Johnson',
    accountName: 'Legal Associates LLP',
    industry: 'Legal',
    lastContact: '2025-01-02T16:30:00Z',
    createdDate: '2024-08-05T09:45:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-02T16:30:00Z',
    lastModifiedBy: 'user_emily',
    activeProjects: 2,
    annualRevenue: 2800000,
    auditId: 'AUD-2024-009',
    auditStatus: 'Completed',
    billingAddress: '800 Justice Way, Washington, DC 20001',
    contractEndDate: '2026-02-28'
  },
  {
    id: 'client_011',
    name: 'Maria Garcia',
    email: 'maria.garcia@email.com',
    phone: '(555) 234-5678',
    status: 'Active',
    accountOwner: 'Mike Chen',
    owner: 'Mike Chen',
    accountManager: 'Emily Davis',
    accountName: 'Garcia Personal Account',
    industry: 'Individual',
    lastContact: '2025-01-03T09:00:00Z',
    createdDate: '2024-09-10T10:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-03T09:00:00Z',
    lastModifiedBy: 'user_mike',
    activeProjects: 1,
    annualRevenue: 65000,
    auditId: 'AUD-2024-010',
    auditStatus: 'Completed',
    billingAddress: '123 Maple Ave, Miami, FL 33101',
    contractEndDate: '2025-09-30',
    // Credit Scores
    initialScore: 590,
    currentScore: 670,
    targetScore: 700,
    scoreImprovement: 80
  },
  {
    id: 'client_012',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '(555) 345-6789',
    status: 'Active',
    accountOwner: 'David Wilson',
    owner: 'David Wilson',
    accountManager: 'Michael Chen',
    accountName: 'Johnson Personal Account',
    industry: 'Individual',
    lastContact: '2025-01-04T11:00:00Z',
    createdDate: '2024-10-15T08:30:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-04T11:00:00Z',
    lastModifiedBy: 'user_david',
    activeProjects: 1,
    annualRevenue: 72000,
    auditId: 'AUD-2024-011',
    auditStatus: 'In Progress',
    billingAddress: '456 Pine Street, Seattle, WA 98101',
    contractEndDate: '2025-10-31',
    // Credit Scores
    initialScore: 610,
    currentScore: 695,
    targetScore: 720,
    scoreImprovement: 85
  },
  {
    id: 'client_013',
    name: 'Emily Davis',
    email: 'emily.davis@email.com',
    phone: '(555) 456-7890',
    status: 'Active',
    accountOwner: 'Sarah Johnson',
    owner: 'Sarah Johnson',
    accountManager: 'David Wilson',
    accountName: 'Davis Personal Account',
    industry: 'Individual',
    lastContact: '2025-01-02T15:00:00Z',
    createdDate: '2024-11-20T09:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-02T15:00:00Z',
    lastModifiedBy: 'user_sarah',
    activeProjects: 1,
    annualRevenue: 58000,
    auditId: 'AUD-2024-012',
    auditStatus: 'Not Started',
    billingAddress: '789 Elm Drive, Denver, CO 80201',
    contractEndDate: '2025-11-30',
    // Credit Scores
    initialScore: 545,
    currentScore: 640,
    targetScore: 680,
    scoreImprovement: 95
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedClientListViews = createPersistedMockData(
  'crm_client_list_views',
  defaultClientListViews
);

export const persistedClientRecords = createPersistedMockData(
  'crm_client_records',
  defaultClientRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockClientListViews = defaultClientListViews;
export const mockClientRecords = defaultClientRecords;
